import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'optionCard',
    'contactMethodInput',
    'fileInput',
    'fileInfo',
    'submitButton',
    'templateSelectionArea',
  ]
  static values = { modalId: String }

  connect() {
    this.selectedOption = null
    this.fileSelected = false
    this.templateSelected = false
    this.csvData = null
    this.templateCompatible = true

    // Listen for template selection events
    window.addEventListener(
      'massiveEngagementTemplateSelected',
      this.handleTemplateSelected.bind(this)
    )

    // Listen for template compatibility changes
    window.addEventListener(
      'templateCompatibilityChanged',
      this.handleCompatibilityChanged.bind(this)
    )

    this.updateSubmitButton()
  }

  openModal() {
    if (this.modalIdValue) {
      document.getElementById(this.modalIdValue).showModal()
    }
  }

  closeModal() {
    // Close the dialog modal properly using the configured modal ID
    if (this.modalIdValue) {
      const dialog = document.getElementById(this.modalIdValue)
      if (dialog) {
        dialog.close()
      }
    }
  }

  selectOption(event) {
    // Remove selected class from all option cards
    this.optionCardTargets.forEach((card) => {
      card.classList.remove('border-yellow-500', 'bg-yellow-50')
      card.classList.add('border')
    })

    // Add selected class to clicked card
    const target = event.currentTarget
    target.classList.add('border')
    target.classList.add('border-yellow-500', 'bg-yellow-50')

    // Select the corresponding radio button
    const optionType = target.dataset.optionType
    this.contactMethodInputTargets.forEach((input) => {
      if (input.value === optionType) {
        input.checked = true
      }
    })

    // Store selected option type
    this.selectedOption = optionType

    if (optionType === 'message') {
      if (this.hasTemplateSelectionAreaTarget) {
        this.templateSelectionAreaTarget.classList.remove('hidden')
        // Reset template selection when switching to message
        if (!window.selectedMassiveEngagementTemplate) {
          this.templateSelected = false
        }
      }
    } else {
      if (this.hasTemplateSelectionAreaTarget) {
        this.templateSelectionAreaTarget.classList.add('hidden')
        // Voice calls don't need templates
        this.templateSelected = true
        window.selectedMassiveEngagementTemplate = null
      }
    }

    this.updateSubmitButton()
  }

  async fileSelecteddd(event) {
    const fileInput = event.target
    const fileInfoTarget = this.fileInfoTarget

    if (fileInput.files && fileInput.files[0]) {
      const fileName = fileInput.files[0].name
      const file = fileInput.files[0]

      try {
        // Parse CSV to extract headers and sample data
        const csvText = await this.readFileAsText(file)
        const csvData = this.parseCSVData(csvText)

        this.csvData = csvData
        fileInfoTarget.textContent = `Arquivo selecionado: ${fileName}`
        fileInfoTarget.classList.remove('hidden')
        this.fileSelected = true

        // Check template compatibility if template is selected
        if (window.selectedMassiveEngagementTemplate) {
          await this.checkTemplateCompatibility()
        }
      } catch (error) {
        fileInfoTarget.textContent = `Erro ao processar arquivo: ${error.message}`
        fileInfoTarget.classList.remove('hidden')
        this.fileSelected = false
        this.csvData = null
      }
    } else {
      fileInfoTarget.textContent = 'Nenhum arquivo selecionado'
      fileInfoTarget.classList.add('hidden')
      this.fileSelected = false
      this.csvData = null
    }

    this.updateSubmitButton()
  }

  removeTemplate() {
    window.selectedMassiveEngagementTemplate = null

    const templateDisplay = document.getElementById('selected-template-display')
    if (templateDisplay) {
      templateDisplay.classList.add('hidden')
    }

    this.templateSelected = false
    this.updateSubmitButton()
  }

  openTemplateSelector() {
    const modal = document.getElementById('massive_engagement_template_selector')
    if (modal) {
      modal.showModal()
    }
  }

  async handleTemplateSelected(event) {
    this.templateSelected = true

    // Update the display with selected template info
    const templateDisplay = document.getElementById('selected-template-display')
    const templateName = document.getElementById('selected-template-name')
    const templateBody = document.getElementById('selected-template-body')

    if (event.detail && event.detail.templateName) {
      templateName.textContent = event.detail.templateName
      templateBody.textContent = event.detail.templateBody || 'Template selecionado'
      templateDisplay.classList.remove('hidden')
    }

    // Check template compatibility if CSV is loaded
    if (this.csvData) {
      await this.checkTemplateCompatibility()
    }

    this.updateSubmitButton()
  }

  // Novo método para lidar com a submissão do formulário quando o botão é clicado
  submitForm(event) {
    event.preventDefault()

    // Check if all required fields are selected
    if (!this.selectedOption || !this.fileSelected) {
      return // Não submete se não tiver opção ou arquivo selecionado
    }

    // For message type, check if template is selected
    if (this.selectedOption === 'message' && !window.selectedMassiveEngagementTemplate) {
      alert('Por favor, selecione um template para envio de mensagens.')
      return
    }

    // Mostra o indicador de carregamento
    document.getElementById('loading_indicator').classList.remove('hidden')

    // Submete o formulário via JavaScript
    const form = this.submitButtonTarget.form
    const formData = new FormData(form)

    // Add selected template ID if available
    if (window.selectedMassiveEngagementTemplate) {
      formData.append('template_id', window.selectedMassiveEngagementTemplate)
    }

    const modalId = this.modalIdValue // Guarda referência ao ID do modal

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        Accept: 'text/html, application/xhtml+xml',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Erro na resposta do servidor')
        }
        return response.text()
      })
      .then((html) => {
        // Parse o HTML retornado
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        // Atualiza apenas o container principal
        const mainContainer = document.querySelector('main') || document.body
        const newContent = doc.querySelector('main') || doc.body

        if (mainContainer && newContent) {
          mainContainer.innerHTML = newContent.innerHTML
        }

        // Reabre o modal após a atualização
        setTimeout(() => {
          const modal = document.getElementById('my_modal_1')
          if (modal) {
            modal.showModal()

            // Ensure modal is visible and scrolled to top if it has validation results
            const modalContent = modal.querySelector('.modal-box')
            if (modalContent) {
              modalContent.scrollTop = 0
            }
          }
        }, 200)
      })
      .catch((error) => {
        document.getElementById('loading_indicator').classList.add('hidden')
        this.fileInfoTarget.textContent = `Erro: ${error.message}`
        this.fileInfoTarget.classList.remove('hidden')
      })
  }

  updateSubmitButton() {
    if (!this.hasSubmitButtonTarget) return

    const submitButton = this.submitButtonTarget

    // Check if we have template selection when needed
    const needsTemplate = this.selectedOption === 'message'
    const hasTemplate = window.selectedMassiveEngagementTemplate || this.templateSelected
    const templateReady = !needsTemplate || hasTemplate

    const allReady =
      this.selectedOption && this.fileSelected && templateReady && this.templateCompatible

    if (allReady) {
      submitButton.disabled = false
      submitButton.classList.remove('opacity-50', 'cursor-not-allowed')
    } else {
      submitButton.disabled = true
      submitButton.classList.add('opacity-50', 'cursor-not-allowed')
    }
  }

  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => resolve(e.target.result)
      reader.onerror = (e) => reject(new Error('Erro ao ler o arquivo'))
      reader.readAsText(file, 'utf-8')
    })
  }

  parseCSVData(csvText) {
    const lines = csvText.trim().split('\n')
    if (lines.length === 0) {
      throw new Error('Arquivo CSV vazio')
    }

    // Detect delimiter (comma vs semicolon)
    const firstLine = lines[0]
    const delimiter = firstLine.split(',').length > firstLine.split(';').length ? ',' : ';'

    // Parse headers
    const headers = lines[0].split(delimiter).map((h) => h.trim().replace(/['"]/g, ''))

    // Parse first data row as sample
    const sampleRow = {}
    if (lines.length > 1) {
      const values = lines[1].split(delimiter).map((v) => v.trim().replace(/['"]/g, ''))
      headers.forEach((header, index) => {
        sampleRow[header] = values[index] || ''
      })
    }

    return {
      headers: headers,
      sample_row: sampleRow,
    }
  }

  async checkTemplateCompatibility() {
    if (!this.csvData || !window.selectedMassiveEngagementTemplate) {
      this.templateCompatible = true
      return
    }

    // Show the beautiful preview modal when both CSV and template are loaded
    this.showPreviewModal()
  }

  showPreviewModal() {
    if (!this.csvData || !window.selectedMassiveEngagementTemplate) return

    // Get the template preview controller
    const previewController = this.application.getControllerForElementAndIdentifier(
      document.getElementById('template_preview_modal'),
      'template-preview'
    )

    if (previewController) {
      previewController.showPreview(window.selectedMassiveEngagementTemplate, this.csvData)
    } else {
      // Fallback - create a new instance
      import('./template_preview_controller.js').then((module) => {
        const PreviewController = module.default
        const controller = new PreviewController()
        controller.showPreview(window.selectedMassiveEngagementTemplate, this.csvData)
      })
    }
  }

  showTemplatePreview(result) {
    // Show preview in template display area
    const templateBody = document.getElementById('selected-template-body')
    if (templateBody && result.preview) {
      templateBody.textContent = result.preview.preview_text
      templateBody.classList.remove('text-red-600')
      templateBody.classList.add('text-green-700')
    }
  }

  showCompatibilityErrors(errors) {
    // Show errors in template display area
    const templateBody = document.getElementById('selected-template-body')
    if (templateBody) {
      templateBody.textContent = `⚠️ ${errors[0] || 'Template incompatível com CSV'}`
      templateBody.classList.remove('text-green-700')
      templateBody.classList.add('text-red-600')
    }
  }

  handleCompatibilityChanged(event) {
    this.templateCompatible = event.detail.compatible
    this.updateSubmitButton()

    // Update UI feedback
    const templateBody = document.getElementById('selected-template-body')
    if (templateBody) {
      if (event.detail.compatible) {
        templateBody.classList.remove('text-red-600')
        templateBody.classList.add('text-green-700')
        templateBody.textContent =
          'Template compatível - clique em "Continuar" no preview para prosseguir'
      } else {
        templateBody.classList.remove('text-green-700')
        templateBody.classList.add('text-red-600')
        templateBody.textContent =
          'Template incompatível com o CSV - verifique as colunas necessárias'
      }
    }
  }
}
